<template>
  <div>
    <!-- Backdrop -->
    <div 
      v-if="isOpen" 
      class="faq-backdrop"
      @click="closeFAQ"
    ></div>
    
    <!-- FAQ Panel - positioned fixed to viewport -->
    <div 
      :class="[
        'faq-panel',
        isOpen ? 'faq-panel-open' : 'faq-panel-closed'
      ]"
    >
      <!-- Header -->
      <div class="faq-header">
        <h1 class="faq-title">FAQs</h1>
        <button 
          @click="closeFAQ" 
          class="btn-close-circle"
        >
          ×
        </button>
      </div>
      
      <!-- Content -->
      <div class="faq-content">
        
        <!-- Home Dashboard Section -->
        <div class="faq-section">
          <h2 class="faq-section-title">Home Dashboard</h2>
          
          <!-- Game Developers Subsection -->
          <div class="faq-subsection">
            <h3 class="faq-subsection-title">Game Developers</h3>
            
            <div class="faq-items">
              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('dashboard-start-playtest')"
                  class="faq-question"
                >
                  <span class="faq-question-text">How do I start a playtest?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['dashboard-start-playtest'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['dashboard-start-playtest']" class="faq-answer">
                  On your Dashboard, click Add New Project. Complete the project information and confirm. Once a Project is created, it will appear on your Dashboard. Click View Details to progress and invite playtesters, create a game-play session and create/share a survey.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('dashboard-status')"
                  class="faq-question"
                >
                  <span class="faq-question-text">How can I quickly see the status of all my ongoing playtests?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['dashboard-status'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['dashboard-status']" class="faq-answer">
                  The Dashboard provides an overview of all your active playtests, showing their current status, number of participants, and key performance metrics at a glance.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('dashboard-customize')"
                  class="faq-question"
                >
                  <span class="faq-question-text">Can I customize the information displayed on my dashboard?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['dashboard-customize'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['dashboard-customize']" class="faq-answer">
                  No, you can not configure your dashboard. If there are features you'd like to see please write to us so we can prioritize development most important to you.
                </div>
              </div>
            </div>
          </div>

          <!-- Playtesters Subsection -->
          <div class="faq-subsection">
            <h3 class="faq-subsection-title">Playtesters</h3>
            
            <div class="faq-items">
              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('find-games')"
                  class="faq-question"
                >
                  <span class="faq-question-text">How can I find games to test?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['find-games'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['find-games']" class="faq-answer">
                  From the Dashboard Task Bar click Discover and search for games that match your profile and preferences. You can easily browse and apply for new sessions.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('scheduled-info')"
                  class="faq-question"
                >
                  <span class="faq-question-text">Where can I find information about my scheduled playtests?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['scheduled-info'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['scheduled-info']" class="faq-answer">
                  The Dashboard includes Assignments Due and New Assignments section that shows all your upcoming playtest appointments, including dates, times, and specific instructions.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('track-progress')"
                  class="faq-question"
                >
                  <span class="faq-question-text">Can I track my progress and achievements on the dashboard?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['track-progress'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['track-progress']" class="faq-answer">
                  Yes, from the dashboard Task Bar you can navigate to Achievements which provides a summary of your completed playtests, surveys completed, and, in the future, a reputation score, giving you a clear overview of your activity on the platform.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('update-profile')"
                  class="faq-question"
                >
                  <span class="faq-question-text">How do I update my profile and gaming preferences?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['update-profile'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['update-profile']" class="faq-answer">
                  A dedicated section on the dashboard Task Bar allows you to easily edit your Profile, update your gaming preferences, and ensure you're matched with the most relevant playtests.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('miss-playtest')"
                  class="faq-question"
                >
                  <span class="faq-question-text">What if I miss a scheduled playtest?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['miss-playtest'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['miss-playtest']" class="faq-answer">
                  The dashboard will alert you to any assigned sessions and they will stay open until they are completed.
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Search Function Section -->
        <div class="faq-section">
          <h2 class="faq-section-title">Search Function</h2>
          
          <!-- Game Developers Subsection -->
          <div class="faq-subsection">
            <h3 class="faq-subsection-title">Game Developers</h3>
            
            <div class="faq-items">
              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('find-playtesters')"
                  class="faq-question"
                >
                  <span class="faq-question-text">How do I find a specific group of playtesters for my game?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['find-playtesters'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['find-playtesters']" class="faq-answer">
                  Click Add New Playtester. The search function allows you to filter potential playtesters by demographics, genre preferences, and specific gameplay skills to find the perfect match for your target audience. Add playtesters to join your project and an invitation will be sent through our platform.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('search-criteria')"
                  class="faq-question"
                >
                  <span class="faq-question-text">What criteria can I use to narrow down my search?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['search-criteria'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['search-criteria']" class="faq-answer">
                  You can refine your search using various criteria, including age, platform (PC, Console, Mobile), genre, time spent playing, etc.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('quality-assurance')"
                  class="faq-question"
                >
                  <span class="faq-question-text">How does the platform ensure the quality of playtesters?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['quality-assurance'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['quality-assurance']" class="faq-answer">
                  We use a verification process to ensure they are real people, and track playtester performance and reliability to ensure you are connected with high-quality, engaged participants. We are still building a reliable community, any advice or recommendations would be helpful.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('save-criteria')"
                  class="faq-question"
                >
                  <span class="faq-question-text">Can I save my search criteria for future use?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['save-criteria'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['save-criteria']" class="faq-answer">
                  Not at the moment, but we're working on it. So, please remember your intended player profile.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('recruit-specific')"
                  class="faq-question"
                >
                  <span class="faq-question-text">How do I recruit a specific playtester I've worked with before?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['recruit-specific'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['recruit-specific']" class="faq-answer">
                  You can search for individual playtesters by their Nickname and invite them to participate in new playtests, building a reliable pool of testers for your studio.
                </div>
              </div>
            </div>
          </div>

          <!-- Discover Subsection -->
          <div class="faq-subsection">
            <h3 class="faq-subsection-title">Discover</h3>
            
            <div class="faq-subsection-content">
              <h4 class="faq-sub-subsection-title">Playtesters</h4>
              
              <div class="faq-items">
                <div class="faq-item">
                  <button 
                    @click="toggleFAQItem('find-game-interests')"
                    class="faq-question"
                  >
                    <span class="faq-question-text">How do I find a game to playtest that fits my interests?</span>
                    <svg 
                      :class="['faq-chevron', openFAQs['find-game-interests'] ? 'faq-chevron-open' : '']" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div v-if="openFAQs['find-game-interests']" class="faq-answer">
                    You can use the Search by Tag function to filter for games by their playtesting purpose (e.g. usability, design, performance, etc.).
                  </div>
                </div>

                <div class="faq-item">
                  <button 
                    @click="toggleFAQItem('search-session-types')"
                    class="faq-question"
                  >
                    <span class="faq-question-text">Can I search for specific types of playtest sessions?</span>
                    <svg 
                      :class="['faq-chevron', openFAQs['search-session-types'] ? 'faq-chevron-open' : '']" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div v-if="openFAQs['search-session-types']" class="faq-answer">
                    Not at the moment, but if you have any suggestions please write us an email, we'd love to hear your thoughts.
                  </div>
                </div>

                <div class="faq-item">
                  <button 
                    @click="toggleFAQItem('apply-playtest')"
                    class="faq-question"
                  >
                    <span class="faq-question-text">How do I apply for a playtest once I find one I like?</span>
                    <svg 
                      :class="['faq-chevron', openFAQs['apply-playtest'] ? 'faq-chevron-open' : '']" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div v-if="openFAQs['apply-playtest']" class="faq-answer">
                    The search results will have a Join button, and after clicking it, a notice will be sent through our platform to the Developer and the project will appear on your Dashboard.
                  </div>
                </div>

                <div class="faq-item">
                  <button 
                    @click="toggleFAQItem('selection-notification')"
                    class="faq-question"
                  >
                    <span class="faq-question-text">How will I be notified if I've been selected for a playtest?</span>
                    <svg 
                      :class="['faq-chevron', openFAQs['selection-notification'] ? 'faq-chevron-open' : '']" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div v-if="openFAQs['selection-notification']" class="faq-answer">
                    You will receive a notification on your dashboard once you have been selected for a playtest. Playtest details will be on the Project Card.
                  </div>
                </div>

                <div class="faq-item">
                  <button 
                    @click="toggleFAQItem('see-requirements')"
                    class="faq-question"
                  >
                    <span class="faq-question-text">Is it possible to see the requirements for a playtest before I apply?</span>
                    <svg 
                      :class="['faq-chevron', openFAQs['see-requirements'] ? 'faq-chevron-open' : '']" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div v-if="openFAQs['see-requirements']" class="faq-answer">
                    Yes, each Project includes a detailed description of the game, the required hardware or software, and the specific tasks you will need to complete.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Survey Tool Section -->
        <div class="faq-section">
          <h2 class="faq-section-title">Survey Tool</h2>
          
          <!-- Game Developers Subsection -->
          <div class="faq-subsection">
            <h3 class="faq-subsection-title">Game Developers</h3>
            
            <div class="faq-items">
              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('create-surveys')"
                  class="faq-question"
                >
                  <span class="faq-question-text">How do I create my own custom surveys?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['create-surveys'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['create-surveys']" class="faq-answer">
                  Our platform provides a customizable survey builder that allows you to create questions tailored to your specific research goals. Click Add Survey and you can choose to make one from scratch or use one of our templates (you can edit these). Once you've completed the survey, click Add to Project. You will see it on your Survey card. Click Submit, it will be sent for approval to ensure it aligns with our Terms of Use. Once approved, you can Publish the survey and it will automatically be sent to the playtesters assigned to the project.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('question-types')"
                  class="faq-question"
                >
                  <span class="faq-question-text">What types of questions can I include in my surveys?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['question-types'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['question-types']" class="faq-answer">
                  Our survey tool supports a variety of question types, including multiple-choice, open-ended text, rating scales, and images.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('analyze-data')"
                  class="faq-question"
                >
                  <span class="faq-question-text">Can I analyze the survey data to find common trends?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['analyze-data'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['analyze-data']" class="faq-answer">
                  The survey tool's analytics feature allows you to visualize responses. You can read individual answers or aggregate all responses making it easy to identify trends and recurring feedback themes. Also, you can download the data (.csv) to share with your team.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('survey-templates')"
                  class="faq-question"
                >
                  <span class="faq-question-text">Are there pre-made survey templates I can use?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['survey-templates'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['survey-templates']" class="faq-answer">
                  Yes, we offer a library of expert-designed survey templates for various stages of game development, from early concept testing to post-launch feedback.
                </div>
              </div>
            </div>
          </div>

          <!-- Playtesters Subsection -->
          <div class="faq-subsection">
            <h3 class="faq-subsection-title">Playtesters</h3>
            
            <div class="faq-items">
              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('access-survey')"
                  class="faq-question"
                >
                  <span class="faq-question-text">How do I access the survey for my playtest session?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['access-survey'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['access-survey']" class="faq-answer">
                  The survey link is provided on your session dashboard and in the instructions for each playtest.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('edit-survey')"
                  class="faq-question"
                >
                  <span class="faq-question-text">Can I go back and edit my survey answers after I submit them?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['edit-survey'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['edit-survey']" class="faq-answer">
                  Once a survey is submitted, it is considered final and cannot be edited to ensure the integrity of the data.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('survey-question-types')"
                  class="faq-question"
                >
                  <span class="faq-question-text">What kind of questions can I expect in a survey?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['survey-question-types'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['survey-question-types']" class="faq-answer">
                  Surveys may include questions about your overall experience, specific gameplay mechanics, user interface feedback, and your general impressions of the game.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('survey-anonymity')"
                  class="faq-question"
                >
                  <span class="faq-question-text">Are my survey responses anonymous?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['survey-anonymity'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['survey-anonymity']" class="faq-answer">
                  While your feedback is linked to your playtest session for the developers' analysis, your personal identifying information is kept confidential. Only your Nickname is seen.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('survey-feedback-help')"
                  class="faq-question"
                >
                  <span class="faq-question-text">How does my survey feedback help the developers?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['survey-feedback-help'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['survey-feedback-help']" class="faq-answer">
                  Your feedback provides valuable insights into the player experience, helping developers understand what's working and what needs improvement in their game.
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Gameplay Section -->
        <div class="faq-section">
          <h2 class="faq-section-title">Gameplay</h2>
          
          <!-- Game Developers Subsection -->
          <div class="faq-subsection">
            <h3 class="faq-subsection-title">Game Developers</h3>
            
            <div class="faq-items">
              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('setup-playtest')"
                  class="faq-question"
                >
                  <span class="faq-question-text">How do I set up a new playtest session?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['setup-playtest'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['setup-playtest']" class="faq-answer">
                  You can easily create a new playtest session, click Add New Playsession. Complete testing information including dates, game link and specific tasks for the player. Once a playtest is created it will appear on your Gameplay card. To add testers to your playtest, simply click Add Testers, then check the box next to each player from your project you'd like to see participate.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('record-sessions')"
                  class="faq-question"
                >
                  <span class="faq-question-text">How does the platform record playtest sessions?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['record-sessions'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['record-sessions']" class="faq-answer">
                  The platform uses an automated tool to record the playtester's screen, audio, and webcam, giving you a full view of their experience.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('add-annotations')"
                  class="faq-question"
                >
                  <span class="faq-question-text">Can I add annotations or comments to the gameplay recordings?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['add-annotations'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['add-annotations']" class="faq-answer">
                  Yes, you can add timestamped annotations directly to the video recordings to highlight specific moments, behaviors, or feedback points for your team to review. When you write Instructions for the playtest, the user will drag and drop those tasks into the timeline when they have completed the play session.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('share-recordings')"
                  class="faq-question"
                >
                  <span class="faq-question-text">How do I share the gameplay recordings with my team?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['share-recordings'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['share-recordings']" class="faq-answer">
                  All playtest recordings are securely stored and can be downloaded and shared with team members, allowing for collaborative analysis and feedback.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('behavior-data')"
                  class="faq-question"
                >
                  <span class="faq-question-text">Does the platform provide data on player behavior beyond the recordings?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['behavior-data'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['behavior-data']" class="faq-answer">
                  Not yet, but we are working on it. What type of information would you like to see? Please write and let us know.
                </div>
              </div>
            </div>
          </div>

          <!-- Playtesters Subsection -->
          <div class="faq-subsection">
            <h3 class="faq-subsection-title">Playtesters</h3>
            
            <div class="faq-items">
              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('start-playtest-session')"
                  class="faq-question"
                >
                  <span class="faq-question-text">How do I start a playtest session?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['start-playtest-session'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['start-playtest-session']" class="faq-answer">
                  You'll find a notice beside the Start button on your dashboard for your scheduled playtest. This will open a window with instructions. Choose the Playtest Period from the drop-down. Open the game link in a new tab or download. Click Start New Playtest. The Help button will take you through our features. Click Audio & Visual to record gameplay and approve/deny permissions for your microphone and webcam. Click Start Gameplay Record.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('what-to-do')"
                  class="faq-question"
                >
                  <span class="faq-question-text">What do I do during a playtest?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['what-to-do'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['what-to-do']" class="faq-answer">
                  The developer will provide you with some instructions and objectives for the gameplay. Once you have started a playtest, continue until you have completed the instructions provided. When finished playing, return to Pixels to Players, click Stop and Save Recording. After the recording has ended drag & drop the Instructions into the video timeline.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('encounter-bug')"
                  class="faq-question"
                >
                  <span class="faq-question-text">What happens if I encounter a bug during a playtest?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['encounter-bug'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['encounter-bug']" class="faq-answer">
                  Once you have started a playtest, continue until you have completed the instructions provided. After the recording has ended you can report bugs…click Bugs → Add Bug. Drag & drop them into the video timeline.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('special-software')"
                  class="faq-question"
                >
                  <span class="faq-question-text">Do I need to install any special software to participate?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['special-software'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['special-software']" class="faq-answer">
                  Pixels to Players requires no special software. Only a link to the game build is required. However, the developer may require software and include a direct download of the game build.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('pause-session')"
                  class="faq-question"
                >
                  <span class="faq-question-text">Can I pause or stop a playtest session once it has begun?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['pause-session'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['pause-session']" class="faq-answer">
                  Yes, you can pause the session if needed. When recording gameplay it is best to do it in one sitting. Please be sure to read the instructions on how to properly end the session once you have completed all the required tasks.
                </div>
              </div>

              <div class="faq-item">
                <button 
                  @click="toggleFAQItem('playing-correctly')"
                  class="faq-question"
                >
                  <span class="faq-question-text">How do I know if I'm playing the game correctly for the test?</span>
                  <svg 
                    :class="['faq-chevron', openFAQs['playing-correctly'] ? 'faq-chevron-open' : '']" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div v-if="openFAQs['playing-correctly']" class="faq-answer">
                  Each playtest includes a set of clear instructions and objectives to guide you, ensuring you know exactly what the developers need you to do during gameplay. When you have finished playing, drag and drop those tasks/instructions into the timeline. Then Submit Recording.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FAQPanel',
  data() {
    return {
      isOpen: false,
      openFAQs: {}
    }
  },
  mounted() {
    // Listen for escape key to close panel
    document.addEventListener('keydown', this.handleEscapeKey);
  },
  beforeDestroy() {
    // Clean up event listeners
    document.removeEventListener('keydown', this.handleEscapeKey);
  },
  methods: {
    // Method to toggle the entire FAQ panel
    toggleFAQ() {
      this.isOpen = !this.isOpen;
      // Prevent body scrolling when panel is open
      document.body.style.overflow = this.isOpen ? 'hidden' : '';
    },
    // Method to open the FAQ panel
    openFAQ() {
      this.isOpen = true;
      // Prevent body scrolling when panel is open
      document.body.style.overflow = 'hidden';
    },
    // Method to close the FAQ panel
    closeFAQ() {
      this.isOpen = false;
      // Restore body scrolling
      document.body.style.overflow = '';
    },
    // Method to toggle individual FAQ items
    toggleFAQItem(faqId) {
      this.$set(this.openFAQs, faqId, !this.openFAQs[faqId]);
    },
    handleEscapeKey(event) {
      if (event.key === 'Escape' && this.isOpen) {
        this.closeFAQ();
      }
    }
  }
}
</script>

<style scoped>
/* Backdrop */
.faq-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

/* FAQ Panel */
.faq-panel {
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 500px;
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.95) 0%, 
    rgba(248, 250, 252, 0.98) 100%);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  box-shadow: 
    -10px 0 40px rgba(255, 140, 0, 0.15),
    -4px 0 20px rgba(255, 140, 0, 0.1),
    inset 1px 0 0 rgba(255, 165, 0, 0.4);
  z-index: 1001;
  transform: translateX(100%);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  border-left: 1px solid rgba(255, 165, 0, 0.2);
}

.faq-panel-open {
  transform: translateX(0);
}

.faq-panel-closed {
  transform: translateX(100%);
}

/* Header */
.faq-header {
  background: linear-gradient(135deg, #5226DF 0%, #FF7222 100%);
  color: white;
  padding: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.faq-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.1) 50%, transparent 70%);
  animation: shimmer 3s ease-in-out infinite;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.faq-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.025em;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1;
}



/* Content */
.faq-content {
  height: calc(100vh - 120px);
  overflow-y: auto;
  padding: 2rem;
  padding-bottom: 3rem;
}

/* Scrollbar styles inherited from global App.vue */

/* Sections */
.faq-section {
  margin-bottom: 3rem;
}

.faq-section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  position: relative;
}

.faq-section-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: linear-gradient(90deg, #ff8c00, #ff6600);
  border-radius: 2px;
}

/* Subsections */
.faq-subsection {
  margin-bottom: 2rem;
}

.faq-subsection-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 1rem;
  padding-left: 1rem;
  border-left: 4px solid #ff8c00;
  position: relative;
}

.faq-subsection-content {
  margin-left: 1rem;
}

.faq-sub-subsection-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 0.75rem;
  margin-top: 1rem;
  padding-left: 0.75rem;
  border-left: 3px solid #ffb366;
}

/* FAQ Items */
.faq-items {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.faq-item {
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 140, 0, 0.1);
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.faq-item:hover {
  border-color: rgba(255, 140, 0, 0.3);
  box-shadow: 0 8px 25px rgba(255, 140, 0, 0.15);
  transform: translateY(-2px);
}

.faq-question {
  width: 100%;
  padding: 1rem 1.25rem;
  text-align: left;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.faq-question:hover {
  background: linear-gradient(135deg, rgba(255, 140, 0, 0.05), rgba(255, 102, 0, 0.05));
}

.faq-question-text {
  font-size: 1rem;
  font-weight: 500;
  color: #2d3748;
  line-height: 1.5;
  margin-right: 1rem;
}

.faq-chevron {
  width: 20px;
  height: 20px;
  color: #ff8c00;
  transform: rotate(0deg);
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  flex-shrink: 0;
}

.faq-chevron-open {
  transform: rotate(180deg);
  color: #ff6600;
}

.faq-answer {
  padding: 0 1.25rem 1.25rem 1.25rem;
  font-size: 0.95rem;
  color: #4a5568;
  line-height: 1.6;
  background: rgba(248, 250, 252, 0.5);
  margin-top: 0.5rem;
  border-top: 1px solid rgba(255, 140, 0, 0.1);
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { 
    opacity: 0; 
    transform: translateY(-10px);
  }
  to { 
    opacity: 1; 
    transform: translateY(0);
  }
}

@media (max-width: 600px) {
  .faq-panel {
    width: 100vw;
    left: 0;
    right: 0;
  }
  
  .faq-title {
    font-size: 1.75rem;
  }
  
  .faq-section-title {
    font-size: 1.25rem;
  }
  
  .faq-content {
    padding: 1.5rem;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/layout/FAQ.vue